<template>
  <section class="map-filter component">
    <accessible-tabs v-if="showFilter">
      <ul
        role="tablist"
        class="filter-items container"
      >
        <li
          class="filter-item tab-group-button"
          role="presentation"
          :class="[{ active: !currentRegion }]"
          @click="handleRegionClick()"
          @keypress.enter.prevent="handleRegionClick()"
          @keypress.space.prevent="handleRegionClick()"
        >
          {{ tc("booking.allRegions") }}
        </li>
        <li
          v-for="(region, i) in regions"
          :key="region.Id"
          class="filter-item"
          role="presentation"
          :class="[{ active: currentRegion === region.Id }]"
        >
          <a
            :id="'region-filter-' + i"
            href=""
            class="tab-group-button"
            role="tab"
            @click.prevent="handleRegionClick(region.Id)"
            @keypress.enter.prevent="handleRegionClick(region.Id)"
            @keypress.space.prevent="handleRegionClick(region.Id)"
          >
            {{ region.Title }}
          </a>
        </li>
        <li
          v-for="(externalMap, i) in externalMaps"
          :key="externalMap.Title + i"
          class="filter-item"
          role="presentation"
        >
          <a
            :id="'region-filter-' + i"
            :href="externalMap.Link"
            target="_self"
            class="tab-group-button"
            role="tab"
          >
            {{ externalMap.Title }}
          </a>
        </li>
      </ul>
    </accessible-tabs>
    <div class="map-filter-wrapper">
      <transition
        v-if="showFilter"
        name="slide-left"
      >
        <div
          v-if="showRegionList"
          class="map-filter-list-wrapper container"
        >
          <div
            v-for="(region, key) in filteredItemsByRegion"
            :key="key"
            class="region"
          >
            <span class="region-heading">{{ key }}</span>
            <ul class="cities">
              <li
                v-for="(property, index) in region"
                :key="index"
                :data-name="property.Title"
              >
                <a
                  tabindex="0"
                  :class="[property.Id === currentPropertyId ? 'active' : '']"
                  @click.prevent="handlePropertyClick(property.Id)"
                  @keypress.enter.prevent="handlePropertyClick(property.Id)"
                  @keypress.space.prevent="handlePropertyClick(property.Id)"
                >{{ property.Title }}</a>
              </li>
            </ul>
          </div>
        </div>
      </transition>
      <div
        id="google-map"
        class="map google-map component"
      >
        <google-map
          ref="mapRef"
          :properties="filteredItems"
          :options="options"
          @loaded="handleMapLoaded"
        />
      </div>
    </div>
  </section>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ListMixin from '@mixins/list-mixin'

  export default {
    name: 'MapFilter',
    mixins: [ListMixin],
    props: {
      showFilter: {
        type: Boolean,
      },
      regions: {
        type: Array,
        required: false,
        default: () => [],
      },
      externalMaps: {
        type: Array,
        required: false,
        default: () => [],
      },
      options: {
        type: Object,
        required: false,
        default: () => ({}),
      },
    },
    data() {
      return {
        mapIsLoading: false,
      }
    },
    computed: {
      ...mapState('responsive', ['width']),
      ...mapGetters('i18n', ['tc']),
      isMobile() {
        return this.width <= 1024
      },
      showRegionList() {
        return this.currentRegion || this.isMobile
      },
      currentRegion() {
        return this.$route.query.region
      },
      currentPropertyId() {
        return this.$route.query.property
      },
      filteredItemsByRegion() {
        const groupedProperties = {}
        this.filteredItems.forEach((property) => {
          const region = property.Region
          if (!groupedProperties[region]) {
            groupedProperties[region] = []
          }
          groupedProperties[region].push(property)
        })
        return groupedProperties
      },
    },
    watch: {
      properties: {
        handler(val) {
          this.$refs.list.loadItems({ items: val, total: val.length })
        },
        deep: true,
      },
      currentRegion(val) {
        this.filterProperties(val ? { RegionId: val } : {})
        const region = this.regions.find((region) => region.Id === val)
        if (region) {
          this.$refs.mapRef.resetZoom()
          setTimeout(() => {
            this.$refs.mapRef.smoothZoomToLatLng(this.$refs.mapRef.map, 4, {
              lat: Number(region.Latitude),
              lng: Number(region.Longitude),
            })
          }, 500)
        } else {
          this.$refs.mapRef.resetZoom()
        }
      },
      isMounted(val) {
        if (val) {
          this.initFilter()
        }
      },
    },
    mounted() {
      this.mapIsLoading = true

      this.filterProperties(
        this.currentRegion ? { RegionId: this.currentRegion } : {}
      )

      this.$nextTick(() => {
        this.mapIsLoading = false
      })
    },
    methods: {
      initFilter() {
        this.applyRegionFilter()
      },
      filterProperties(filter) {
        this.updateFilter(filter)
      },
      applyRegionFilter() {
        if (this.currentRegion) {
          this.updateFilter({ RegionId: this.currentRegion })
        } else if (!this.currentRegion && Object.keys(this.filters).length > 0) {
          this.updateFilter({})
        }
      },
      handleRegionClick(regionId) {
        if (regionId) {
          const queryParams = {}
          queryParams.region = regionId
          if (this.$refs.mapRef.map) this.$refs.mapRef.closeMapContent()
          this.$router.push({ query: queryParams })
        } else {
          this.$router.push({ query: {} })
        }
      },
      handlePropertyClick(propertyId) {
        const queryParams = { ...this.$route.query }
        queryParams.property = propertyId
        this.$router.push({ query: queryParams })
        this.clickOnMapMarker(propertyId)
      },
      clickOnMapMarker(propertyId) {
        const propertyMatch = this.serverItems.find(
          (property) => property.Id === propertyId
        )
        const matchingMarker = this.$refs.mapRef.markers.find(
          (marker) => marker.title === propertyMatch.Title
        )
        this.$refs.mapRef.onSelectMarker(
          this.$refs.mapRef.map,
          matchingMarker,
          propertyMatch,
          {
            lat: Number(propertyMatch.Latitude),
            lng: Number(propertyMatch.Longitude),
          }
        )
      },
      handleMapLoaded() {
        if (this.currentRegion) {
          const region = this.regions.find(
            (region) => region.Id === this.currentRegion
          )
          if (region) {
            this.$refs.mapRef.smoothZoomToLatLng(this.$refs.mapRef.map, 3, {
              lat: Number(region.Latitude),
              lng: Number(region.Longitude),
            })
          }
        }
        if (this.$route && this.$route.query.property) {
          this.clickOnMapMarker(this.currentPropertyId)
        }
      },
    },
  }
</script>

<style scoped>
section {
  height: 100%;
  width: 100%;
}
</style>
